import React from 'react';
import { Check, ImagePlus, Video, X } from 'lucide-react';
import { api, type Mark } from '../api.ts';
import { RADIUS, TYPE, useTheme } from '../theme';
import { Badge, Button, Card, Field, Icon, IconButton, Input, SectionHeader, Select, Textarea } from '../components/ui';

/** Formulärfälten för markens uppgifter. Staten ligger i App.tsx (MyMarkDetailView). */
export type MarkInfoForm = Record<'name' | 'description' | 'areaHa' | 'county' | 'municipality' | 'address' | 'contactEmail' | 'contactPhone' | 'contactName' | 'pricePerYear' | 'gameTypesJson' | 'difficulty' | 'highlightsJson' | 'includesJson' | 'annualDrivenHunts' | 'afterhuntHuntersCount' | 'videoUrl' | 'fastighetsBeteckning', string>;

const SPECIES: [string, string][] = [['älg', 'Älg'], ['dovhjort', 'Dovhjort'], ['kronhjort', 'Kronhjort'], ['rådjur', 'Rådjur'], ['vildsvin', 'Vildsvin'], ['fasan', 'Fasan'], ['björn', 'Björn'], ['lo', 'Lo'], ['räv', 'Räv'], ['hare', 'Hare'], ['and', 'And'], ['kanadagås', 'Kanadagås'], ['grågås', 'Grågås']];
const FACILITIES: [string, string][] = [['slaktrum', 'Slaktrum'], ['kylrum', 'Kylrum'], ['eldstad', 'Eldstad'], ['atlar', 'Åtlar'], ['viltkameror', 'Viltkameror'], ['jaktkojor', 'Jaktkoja'], ['pass_torn', 'Pass m. torn'], ['pass_stol', 'Pass m. stol'], ['markpass', 'Markpass'], ['overnattning', 'Övernattning'], ['bastu', 'Bastu'], ['badtunna', 'Badtunna']];

const listValue = (json: string) => { try { return (JSON.parse(json || '[]') as string[]).join(', '); } catch { return ''; } };
const listJson = (text: string) => JSON.stringify(text.split(',').map(s => s.trim()).filter(Boolean));

/** Markens uppgifter ("Inställningar" i titelblocket): sektioner i kort och en fast spararad nederst. */
export function MarkInfoTab(p: {
  mark: Mark; setMark: (m: Mark) => void;
  form: MarkInfoForm; setForm: React.Dispatch<React.SetStateAction<any>>;
  published: boolean; onTogglePublished: () => void;
  err: string; ok: string; uploading: boolean; saving: boolean; onSave: () => void;
  imageUrl: string; setImageUrl: (v: string) => void;
  gallery: string[]; setGallery: React.Dispatch<React.SetStateAction<string[]>>;
  specOcc: Record<string, number>; setSpecOcc: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  facilities: Record<string, number>; setFacilities: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  imgRef: React.RefObject<HTMLInputElement>; gallRef: React.RefObject<HTMLInputElement>; videoRef: React.RefObject<HTMLInputElement>;
  onMainImg: (e: React.ChangeEvent<HTMLInputElement>) => void; onGallery: (e: React.ChangeEvent<HTMLInputElement>) => void; onVideo: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onOpenEkonomi: () => void;
}) {
  const T = useTheme();
  const { form, setForm } = p;
  const set = (k: keyof MarkInfoForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setForm((f: MarkInfoForm) => ({ ...f, [k]: e.target.value }));
  const text = (k: keyof MarkInfoForm, label: string, type = 'text') => <Field label={label}><Input type={type} value={form[k]} onChange={set(k)} /></Field>;
  const list = (k: 'gameTypesJson' | 'highlightsJson' | 'includesJson', label: string, placeholder: string) => (
    <Field label={label} hint="Kommaseparerat"><Input value={listValue(form[k])} placeholder={placeholder} onChange={e => setForm((f: MarkInfoForm) => ({ ...f, [k]: listJson(e.target.value) }))} /></Field>
  );
  const two: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 14 };
  const stack: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 14 };
  const sec = (title: string, children: React.ReactNode, sub?: string) => (
    <section style={{ marginBottom: 24 }}>
      <h3 style={{ ...TYPE.title, color: T.text, margin: '0 0 4px' }}>{title}</h3>
      {sub && <div style={{ ...TYPE.small, color: T.textMuted, marginBottom: 10 }}>{sub}</div>}
      <Card style={{ marginTop: sub ? 0 : 10 }}>{children}</Card>
    </section>
  );
  const removeOnImage: React.CSSProperties = { position: 'absolute', top: 2, right: 2, background: 'rgba(20,28,16,.72)', color: '#fff' };

  const setLevel = (sp: string, n: number) => p.setSpecOcc(prev => {
    const nv = { ...prev };
    if (nv[sp] === n) delete nv[sp]; else nv[sp] = n;
    api.updateMark(p.mark.id, { speciesOccurrenceJson: JSON.stringify(nv) }).then((m: any) => p.setMark(m)).catch(() => { });
    return nv;
  });

  return (
    <div style={{ maxWidth: 880 }}>
      <SectionHeader title="Markens uppgifter" kicker="Inställningar" />

      <Card style={{ marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ ...TYPE.title, fontSize: 16, color: T.text }}>Publicering</span>
            <Badge tone={p.published ? 'ok' : 'neutral'} dot>{p.published ? 'Publicerad' : 'Ej publicerad'}</Badge>
          </div>
          <div style={{ ...TYPE.small, color: T.textMuted, marginTop: 4 }}>{p.published ? 'Marken är publicerad och synlig för jägare.' : 'Marken är avpublicerad och inte synlig för jägare.'}</div>
        </div>
        <Button variant={p.published ? 'secondary' : 'primary'} onClick={p.onTogglePublished}>{p.published ? 'Avpublicera' : 'Publicera'}</Button>
      </Card>

      {p.err && <div role="alert" style={{ background: T.dangerSoft, color: T.danger, borderRadius: RADIUS.md, padding: '12px 14px', fontSize: 14, fontWeight: 600, marginBottom: 16 }}>{p.err}</div>}
      {p.uploading && <div role="status" style={{ background: T.accentSoft, color: T.text, borderRadius: RADIUS.md, padding: '12px 14px', fontSize: 14, marginBottom: 16 }}>Laddar upp fil…</div>}

      {sec('Grundinfo', <div style={stack}>
        {text('name', 'Namn *')}
        <Field label="Beskrivning"><Textarea value={form.description} onChange={set('description')} rows={5} /></Field>
        {text('fastighetsBeteckning', 'Fastighetsbeteckning')}
        <div style={two}>
          {text('areaHa', 'Areal (ha)', 'number')}
          <Field label="Svårighetsgrad">
            <Select value={form.difficulty} onChange={set('difficulty')}>
              <option value="">Välj…</option>
              <option value="Lätt">Lätt</option>
              <option value="Medel">Medel</option>
              <option value="Svår">Svår</option>
              <option value="Expert">Expert</option>
            </Select>
          </Field>
        </div>
        <div style={{ padding: '12px 14px', borderRadius: RADIUS.md, background: T.accentSoft, fontSize: 14, color: T.text, display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>Priser, fällavgifter och kött sätts per grupp under Ekonomi och visas automatiskt i annonserna.</span>
          <Button variant="ghost" size="sm" onClick={p.onOpenEkonomi} style={{ minHeight: 44, color: T.accentText }}>Till Ekonomi →</Button>
        </div>
        {list('gameTypesJson', 'Viltslag', 'ex. älg, dovhjort, vildsvin')}
        {list('highlightsJson', 'Höjdpunkter', 'ex. Stora arealer, Bra vilttillgång')}
        {list('includesJson', 'Ingår', 'ex. Jaktradio, Eftersökstjänst')}
        <div style={two}>
          {text('annualDrivenHunts', 'Drevjakter per år', 'number')}
          {text('afterhuntHuntersCount', 'Eftersöksjägare', 'number')}
        </div>
      </div>)}

      {sec('Bilder och video', <div style={stack}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 8 }}>Huvudbild</div>
          {p.imageUrl && <div style={{ marginBottom: 10, position: 'relative', display: 'inline-block' }}>
            <img src={p.imageUrl} alt="Huvudbild" style={{ width: 220, maxWidth: '100%', height: 140, objectFit: 'cover', borderRadius: RADIUS.md, border: `1px solid ${T.border}`, display: 'block' }} />
            <IconButton label="Ta bort huvudbilden" onClick={() => p.setImageUrl('')} style={removeOnImage}><Icon as={X} size={18} /></IconButton>
          </div>}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Button variant="secondary" icon={<Icon as={ImagePlus} size={18} />} onClick={() => p.imgRef.current?.click()}>Välj bild</Button>
            <input ref={p.imgRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={p.onMainImg} />
          </div>
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 8 }}>Bildgalleri</div>
          {p.gallery.length > 0 && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            {p.gallery.map((url, i) => (
              <div key={i} style={{ position: 'relative' }}>
                <img src={url} alt="" style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: RADIUS.sm, border: `1px solid ${T.border}`, display: 'block' }} />
                <IconButton label={`Ta bort bild ${i + 1}`} onClick={() => p.setGallery(prev => prev.filter((_, j) => j !== i))} style={removeOnImage}><Icon as={X} size={18} /></IconButton>
              </div>
            ))}
          </div>}
          <Button variant="secondary" icon={<Icon as={ImagePlus} size={18} />} onClick={() => p.gallRef.current?.click()}>Lägg till bilder</Button>
          <input ref={p.gallRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={p.onGallery} />
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 8 }}>Video</div>
          {form.videoUrl && <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, minWidth: 0 }}>
            <span style={{ ...TYPE.small, color: T.textMuted, wordBreak: 'break-all' }}>{form.videoUrl}</span>
            <Button variant="ghost" size="sm" onClick={() => setForm((f: MarkInfoForm) => ({ ...f, videoUrl: '' }))} style={{ minHeight: 44, color: T.danger }}>Ta bort</Button>
          </div>}
          <Button variant="secondary" icon={<Icon as={Video} size={18} />} onClick={() => p.videoRef.current?.click()}>Välj video</Button>
          <input ref={p.videoRef} type="file" accept="video/*" style={{ display: 'none' }} onChange={p.onVideo} />
        </div>
      </div>)}

      {sec('Kontakt', <div style={stack}>
        <div style={two}>{text('contactName', 'Kontaktperson')}{text('contactEmail', 'E-post', 'email')}</div>
        {text('contactPhone', 'Telefon', 'tel')}
      </div>)}

      {sec('Plats', <div style={stack}>
        <div style={two}>{text('county', 'Län')}{text('municipality', 'Kommun')}</div>
        {text('address', 'Adress')}
      </div>)}

      {sec('Viltförekomst', (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))', gap: '10px 24px' }}>
          {SPECIES.map(([sp, name]) => {
            const val = p.specOcc[sp] || 0;
            return (
              <div key={sp} role="group" aria-label={`${name}: ${val ? `nivå ${val} av 5` : 'ej angiven'}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ color: T.text, fontSize: 14, fontWeight: 600 }}>{name}</span>
                  <span style={{ ...TYPE.small, color: T.textMuted }}>{val || '–'}</span>
                </div>
                <div style={{ display: 'flex' }}>
                  {[1, 2, 3, 4, 5].map(n => (
                    <button key={n} type="button" aria-label={`${name} nivå ${n}`} aria-pressed={val === n} onClick={() => setLevel(sp, n)}
                      style={{ flex: 1, minWidth: 44, height: 44, padding: '0 2px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                      <span aria-hidden style={{ display: 'block', width: '100%', height: 12, borderRadius: 3, background: n <= val ? T.accentFill : T.surface2, opacity: n <= val ? 0.45 + val * 0.11 : 1, transition: 'background .15s' }} />
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ), 'Välj nivå 1–5. Sparas direkt.')}

      {sec('Faciliteter', (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(170px,1fr))', gap: 8 }}>
          {FACILITIES.map(([key, label]) => {
            const on = (p.facilities[key] || 0) > 0;
            return (
              <button key={key} type="button" aria-pressed={on} onClick={() => p.setFacilities(prev => { const nv = { ...prev }; if (nv[key]) delete nv[key]; else nv[key] = 1; return nv; })}
                style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 44, background: on ? T.accentSoft : T.surface, border: `1px solid ${on ? T.accentFill : T.border}`, borderRadius: RADIUS.md, padding: '8px 12px', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
                <span aria-hidden style={{ width: 20, height: 20, borderRadius: 6, border: `2px solid ${on ? T.accentFill : T.borderHover}`, background: on ? T.accentFill : 'transparent', color: T.onAccent, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {on && <Icon as={Check} size={14} />}
                </span>
                <span style={{ color: T.text, fontSize: 14, fontWeight: on ? 700 : 500 }}>{label}</span>
              </button>
            );
          })}
        </div>
      ), 'Sparas med knappen nedan.')}

      <div style={{ position: 'sticky', bottom: 0, background: T.bg, borderTop: `1px solid ${T.border}`, padding: '12px 0 calc(12px + env(safe-area-inset-bottom, 0px))', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', zIndex: 10 }}>
        <Button size="lg" loading={p.saving} onClick={p.onSave}>{p.saving ? 'Sparar…' : 'Spara uppgifter'}</Button>
        {p.ok && <span role="status" style={{ color: T.ok, fontSize: 14, fontWeight: 600 }}>{p.ok}</span>}
        {p.err && <span style={{ color: T.danger, fontSize: 14, fontWeight: 600 }}>{p.err}</span>}
      </div>
    </div>
  );
}
